/**
 * Cached, deduplicated, cancellable reads.
 *
 * The interactive dashboard's requests had none of that. Every filter press and
 * every per-kg toggle issued a fresh request for data it may have fetched a
 * moment earlier, nothing was cancelled when the surgeon pressed again before a
 * response came back, and the store blanked its data on the way in, so there
 * was never anything to reuse. Stale responses were dropped by re-checking the
 * active case id after the fact, which stops the wrong data rendering but only
 * after the round trip has been paid for.
 *
 * Three rules, in this order:
 *
 * - a fresh cached answer is returned without a request;
 * - a request already in flight for the same key is joined rather than repeated;
 * - a request for a different key in the same `group` supersedes whatever that
 *   group had in flight, and aborts it.
 *
 * The group is what makes variable switching cheap: pressing through four
 * variables leaves one request running, not four.
 */

/** How long a cached answer stays good. */
const DEFAULT_TTL_MS = 60_000;

export interface PerfusionQueryRequest<T> {
  /** Identity of the answer. Equal keys are the same question. */
  key: string;
  /**
   * Requests that supersede one another, e.g. one graph's variable switching.
   * Omit for a request that should not cancel anything.
   */
  group?: string;
  load: (signal: AbortSignal) => Promise<T>;
}

interface CacheEntry {
  value: unknown;
  storedAt: number;
}

interface InFlight {
  promise: Promise<unknown>;
  controller: AbortController;
  key: string;
}

/** Whether a rejection is this module cancelling a superseded request. */
export const isAbortError = (error: unknown): boolean => {
  if (error instanceof DOMException && error.name === 'AbortError') {
    return true;
  }
  const named = error as { name?: string; code?: string };
  return named?.name === 'AbortError' || named?.code === 'ERR_CANCELED';
};

export class PerfusionQuery {
  private readonly cache = new Map<string, CacheEntry>();
  private readonly inFlight = new Map<string, InFlight>();
  /** The one request each group has running. */
  private readonly groups = new Map<string, InFlight>();

  constructor(readonly ttlMs: number = DEFAULT_TTL_MS) {}

  async run<T>(request: PerfusionQueryRequest<T>): Promise<T> {
    const { key, group, load } = request;

    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.storedAt < this.ttlMs) {
      return cached.value as T;
    }

    const running = this.inFlight.get(key);
    if (running) {
      return running.promise as Promise<T>;
    }

    if (group) {
      const superseded = this.groups.get(group);
      // Only a *different* question supersedes: an identical one would have
      // joined above, and aborting a request to reissue it saves nothing.
      if (superseded && superseded.key !== key) {
        superseded.controller.abort();
      }
    }

    const controller = new AbortController();
    const promise = load(controller.signal)
      .then(value => {
        this.cache.set(key, { value, storedAt: Date.now() });
        return value;
      })
      .finally(() => {
        if (this.inFlight.get(key)?.controller === controller) {
          this.inFlight.delete(key);
        }
        if (group && this.groups.get(group)?.controller === controller) {
          this.groups.delete(group);
        }
      });

    const entry: InFlight = { promise, controller, key };
    this.inFlight.set(key, entry);
    if (group) {
      this.groups.set(group, entry);
    }

    return promise as Promise<T>;
  }

  /**
   * Forget cached answers, and abort what is in flight.
   *
   * `keyPrefix` scopes it, so a case change or a manual refresh drops that
   * case's answers without emptying the cache for every other case.
   */
  invalidate(keyPrefix?: string) {
    const matches = (key: string) =>
      keyPrefix === undefined || key.startsWith(keyPrefix);

    for (const key of [...this.cache.keys()]) {
      if (matches(key)) {
        this.cache.delete(key);
      }
    }
    for (const [key, entry] of [...this.inFlight.entries()]) {
      if (matches(key)) {
        entry.controller.abort();
        this.inFlight.delete(key);
      }
    }
  }
}
